import React, { useState, useEffect, useRef } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

const InteractiveDiagram = ({ 
  name,
  alt = 'iot-architecture',
  className = '',
  minScale = 0.5,
  maxScale = 4,
  defaultScale = 0.9,
  limitPanning = false
}) => {
  const [svgContent, setSvgContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const svgContainerRef = useRef(null);
  
  useEffect(() => {
    fetch(`/IMAGE/diagrams/${name}.svg`)
      .then(response => {
        if (!response.ok) throw new Error(`Failed to load diagram: ${response.statusText}`);
        return response.text();
      })
      .then(data => {
        // Process SVG to ensure responsiveness
        let processedSvg = data
          .replace(/<svg[^>]*(width|height)=["'][^"']*["']/g, '<svg') // Remove fixed width/height
          .replace('<svg', '<svg width="100%" height="100%" preserveAspectRatio="xMidYMid meet"'); // Make responsive
        setSvgContent(processedSvg);
        setIsLoading(false);
      })
      .catch(err => {
        console.error(`Error loading SVG: ${err.message}`);
        setError(err.message);
        setIsLoading(false);
      });
  }, [name]);

  if (isLoading) return <div className="text-center p-4">Loading diagram...</div>;
  if (error) return <div className="text-red-500 p-4">Error: {error}</div>;

  // Container style: Fully obey parent’s height constraints
  const containerStyle = {
    width: '100%',
    height: '100%', // Fills IoT.js’s capped container
    position: 'relative',
    margin: 0,
    padding: 0,
    overflow: limitPanning ? 'hidden' : 'visible' // Respect panning limit
  };

  const transformWrapperStyle = {
    width: '100%',
    height: '100%',
    position: 'relative'
  };

  const diagramStyle = {
    width: '100%',
    height: '100%'
  };

  const handleReset = (resetTransform) => resetTransform();

  return (
    <div className={`interactive-diagram ${className}`} style={containerStyle}>
      <TransformWrapper
        initialScale={defaultScale}
        minScale={minScale}
        maxScale={maxScale}
        wheel={{ step: 0.1 }}
        limitToBounds={limitPanning}
        initialPositionX={0}
        initialPositionY={0}
        centerContent={true}
        panning={{ disabled: false, velocityDisabled: false }}
      >
        {({ zoomIn, zoomOut, resetTransform }) => (
          <>
            <div className="controls absolute top-2 right-2 z-10 flex space-x-1">
              <button 
                onClick={() => zoomIn(0.2)} 
                className="px-2 py-0.5 bg-blue-500 text-white rounded hover:bg-blue-600 text-xs opacity-70 hover:opacity-100"
                aria-label="Zoom in"
              >
                +
              </button>
              <button 
                onClick={() => zoomOut(0.2)} 
                className="px-2 py-0.5 bg-blue-500 text-white rounded hover:bg-blue-600 text-xs opacity-70 hover:opacity-100"
                aria-label="Zoom out"
              >
                -
              </button>
              <button 
                onClick={() => handleReset(resetTransform)} 
                className="px-1.5 py-0.5 bg-gray-700 text-white rounded hover:bg-gray-900 text-xs opacity-70 hover:opacity-100"
                aria-label="Reset zoom"
              >
                R
              </button>
            </div>
            
            <div style={transformWrapperStyle}>
              <TransformComponent 
                wrapperStyle={{ width: '100%', height: '100%' }}
                contentStyle={{ width: '100%', height: '100%' }}
              >
                <div 
                  ref={svgContainerRef}
                  style={diagramStyle}
                  dangerouslySetInnerHTML={{ __html: svgContent }}
                  className="svg-container"
                />
              </TransformComponent>
            </div>
          </>
        )}
      </TransformWrapper>
    </div>
  );
};

export default InteractiveDiagram;